<template>
  <div>
    <v-data-table
      ref="dataTable"
      :loading="isLoading || isLoadingSequenceNumbers"
      :headers="SequenceNumberHeaders"
      :items="sequenceNumbers"
      :header-props="{ sortByText: 'ソート' }"
      :footer-props="{ itemsPerPageText: '行/ページ:' }"
      no-data-text="データがありません"
      :page="$route.query.page ? Number($route.query.page) : 1"
      @update:page="($event) => {
        $vuetify.goTo($refs.dataTable)
        addQuery({ page: $event })
      }"
    >
      <template #loading>
        <v-skeleton-loader type="table" />
      </template>

      <template #top>
        <div class="w-full d-flex align-center">
          <v-spacer />
          <v-btn
            :loading="isLoading"
            :disabled="sequenceNumbers.length <= 0 || isLoading"
            :ripple="false"
            color="secondary"
            small
            class="ma-2"
            @click="download"
          >
            <v-icon left>
              {{ icons.mdiFileExcel }}
            </v-icon>
            ダウンロード
          </v-btn>
        </div>
      </template>

      <template #[`item.attributes.tableName`]="{ item }">
        {{ item.attributes.tableName }}
      </template>

      <template #[`item.attributes.number`]="{ item }">
        {{ item.attributes.number || '-' }}
      </template>

      <template #[`item.attributes.receiptId`]="{ item }">
        <v-chip
          v-if="item.attributes.receiptId"
          small
          @click="$emit('open-receipt', item.attributes.receiptId)"
        >
          <clip-loader
            v-if="isLoadingReceipt && +receipt.id === +item.attributes.receiptId"
            :loading="true"
            :color="'#8A8D93'"
            size="16px"
            class="d-flex align-items pr-2"
          />
          伝票 No.{{ item.attributes.receiptId }}
        </v-chip>
        <span v-else>-</span>
      </template>

      <template #[`item.attributes.receiptNo`]="{ item }">
        {{ item.attributes.receiptNo || '-' }}
      </template>

      <template #[`item.status`]="{ item }">
        <div class="d-flex">
          <v-chip
            v-if="item.attributes.tableDiscarded"
            small
            color="warning"
            text
            class="v-chip-light-bg warning--text"
          >
            卓削除済
          </v-chip>
          <v-chip
            v-if="item.attributes.receiptDiscarded"
            small
            color="error"
            text
            class="v-chip-light-bg error--text"
          >
            伝票削除済
          </v-chip>
          <span
            v-if="!item.attributes.tableDiscarded && !item.attributes.receiptDiscarded"
            class="ma-1"
          >
            -
          </span>
        </div>
      </template>

      <template #[`footer.page-text`]="props">
        {{ props.pageStart }} - {{ props.pageStop }} / {{ props.itemsLength }}
      </template>
    </v-data-table>
  </div>
</template>

<script>
import {
  ref,
  getCurrentInstance,
  inject,
} from '@vue/composition-api'
import { mdiFileExcel } from '@mdi/js'
import * as XLSX from 'xlsx/xlsx.mjs'
import ClipLoader from 'vue-spinner/src/ClipLoader.vue'
import SequenceNumberApi from '@/api/waiter/SequenceNumber'
import useDateFormat from '@/views/composable/useDateFormat'
import useQueryString from '@/views/composable/useQueryString'
import useCompInit from '@/views/composable/useCompInit'
import { SequenceNumberHeaders } from '@/utils/constants/table-headers'

export default {
  components: {
    ClipLoader,
  },
  props: {
    date: {
      type: String,
      required: true,
      default: () => new Date().toISOString().substr(0, 10),
    },
    interval: {
      type: Number,
      required: true,
      default: 0,
    },
    endDate: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const vm = getCurrentInstance().proxy

    const isLoadingSequenceNumbers = ref(false)
    const sequenceNumbers = ref([])
    const receipt = inject('receipt', {})
    const isLoadingReceipt = inject('isLoadingReceipt', false)
    const isReceiptOpen = inject('isReceiptOpen', false)
    const isDownloading = ref(false)

    const { dateTime } = useDateFormat()
    const { route, addQuery } = useQueryString()
    const { isLoading, initWith } = useCompInit()

    const getSequenceNumbers = async ({
      date = null,
      interval = null,
      endDate = null,
    }) => {
      isLoadingSequenceNumbers.value = true

      const res = await SequenceNumberApi.getSequenceNumbers({
        date,
        interval,
        endDate,
      })

      if (res?.status === 200) {
        sequenceNumbers.value = [...res.data.tables.data]
      }

      isLoadingSequenceNumbers.value = false
    }

    const buildXlsxData = () => {
      return sequenceNumbers.value.map(item => ({
        卓: item.attributes.tableName,
        連番: item.attributes.number || '',
        伝票ID: item.attributes.receiptId || '',
        伝票No: item.attributes.receiptNo || '',
        卓削除済: item.attributes.tableDiscarded ? 'はい' : 'いいえ',
        伝票削除済: item.attributes.receiptDiscarded ? 'はい' : 'いいえ',
      }))
    }

    const download = () => {
      isDownloading.value = true

      const sequenceNumbersExcel = buildXlsxData()

      const wb = XLSX.utils.book_new()
      const data = XLSX.utils.json_to_sheet(sequenceNumbersExcel)
      XLSX.utils.book_append_sheet(wb, data, '連番管理表')

      let title = `${props.date}_${['日', '週', '月'][props.interval]}`
      if (props.endDate) title = `${props.date}~${props.endDate}`
      XLSX.writeFile(wb, `連番管理表_${title}.xlsx`)
      isDownloading.value = false
    }

    initWith([
      getSequenceNumbers(route.value.query),
    ])

    return {
      // data
      isLoading,
      isLoadingSequenceNumbers,
      sequenceNumbers,
      SequenceNumberHeaders,
      receipt,
      isLoadingReceipt,
      isReceiptOpen,

      // methods
      dateTime,
      addQuery,
      getSequenceNumbers,
      download,

      icons: {
        mdiFileExcel,
      },
    }
  },
}
</script>
